/**
 * MODULE: _shared/frontend-shell/src/features/crm/components/activity/activityFeedBuckets.ts
 * PURPOSE: Group CRM activities into Today / Yesterday / This Week / Earlier (Prompt 3-C)
 * DEPENDENCIES: date-fns, ../../types/activity.types
 * PUBLIC_API: activityDateBucket, groupActivitiesByDateBucket, BUCKET_ORDER, BUCKET_LABELS
 * SIDE_EFFECTS: None
 * AGENT_SAFETY: SAFE
 * FILE_SIZE: ~55/150
 */

import { endOfWeek, isSameDay, isValid, parseISO, startOfWeek, subDays } from 'date-fns';
import type { Activity } from '../../types/activity.types';

export type ActivityDateBucket = 'today' | 'yesterday' | 'thisWeek' | 'earlier';

export const BUCKET_ORDER: ActivityDateBucket[] = ['today', 'yesterday', 'thisWeek', 'earlier'];

export const BUCKET_LABELS: Record<ActivityDateBucket, string> = {
  today: 'Today',
  yesterday: 'Yesterday',
  thisWeek: 'This Week',
  earlier: 'Earlier',
};

export function activityDateBucket(createdAtIso: string, now: Date = new Date()): ActivityDateBucket {
  const d = parseISO(createdAtIso);
  if (!isValid(d)) return 'earlier';
  // Compare to `now` (not system clock only) so feeds and tests are deterministic across TZ.
  if (isSameDay(d, now)) return 'today';
  if (isSameDay(d, subDays(now, 1))) return 'yesterday';
  const weekStart = startOfWeek(now, { weekStartsOn: 1 });
  const weekEnd = endOfWeek(now, { weekStartsOn: 1 });
  if (d >= weekStart && d <= weekEnd) return 'thisWeek';
  return 'earlier';
}

export function groupActivitiesByDateBucket(
  activities: Activity[],
  now: Date = new Date()
): Record<ActivityDateBucket, Activity[]> {
  const buckets: Record<ActivityDateBucket, Activity[]> = {
    today: [],
    yesterday: [],
    thisWeek: [],
    earlier: [],
  };
  const createdMs = (a: Activity): number => {
    const d = parseISO(a.createdAtISO);
    return isValid(d) ? d.getTime() : 0;
  };
  const sorted = [...activities].sort((a, b) => createdMs(b) - createdMs(a));
  for (const a of sorted) {
    buckets[activityDateBucket(a.createdAtISO, now)].push(a);
  }
  return buckets;
}
